/**
 * Admin · IT Solutions · RFQ Inbox.
 *
 * Reads `it_quote_requests` collection via /api/admin/it-catalogue/rfq.
 * Detail panel shows company info, items, notes and quick CRM lead link.
 */

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Inbox, Search, Loader2, Building2, Mail, Phone, Globe, FileText,
  Calendar, Clock, ArrowRight, X, Hash, ExternalLink, Laptop,
} from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  } catch (_) { return v; }
};

const RFQInbox = () => {
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/admin/it-catalogue/rfq`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setRfqs(r.data || []);
      } catch (_) { if (alive) setRfqs([]); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  const filtered = useMemo(() => {
    if (!search) return rfqs;
    const s = search.toLowerCase();
    return rfqs.filter((r) =>
      [r.reference, r.company_name, r.contact_name, r.contact_email, r.country, r.company_trn]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(s))
    );
  }, [rfqs, search]);

  const stats = useMemo(() => ({
    total: rfqs.length,
    items: rfqs.reduce((acc, r) => acc + (r.items || []).length, 0),
    last7days: rfqs.filter((r) => {
      try {
        return Date.now() - new Date(r.created_at).getTime() < 7 * 24 * 3600 * 1000;
      } catch (_) { return false; }
    }).length,
  }), [rfqs]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="mb-4"><BackButton to="/admin/it-catalogue" label="Catalogue" /></div>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Inbox className="w-6 h-6 text-[#023E8A]" />
            <h1 className="text-2xl font-bold text-[#2B2D42]">RFQ Inbox</h1>
            <Badge className="bg-[#023E8A]/10 text-[#023E8A] border border-[#023E8A]/30 font-mono text-[10px]">P0.7</Badge>
          </div>
          <p className="text-sm text-[#6C757D]">
            Incoming quote requests from the public IT Solutions page.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Kpi label="Total RFQs" value={stats.total} icon={Inbox} accent="#023E8A" />
        <Kpi label="Items requested" value={stats.items} icon={Laptop} accent="#FF6B35" />
        <Kpi label="Last 7 days" value={stats.last7days} icon={Clock} accent="#16A34A" />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-[#6C757D] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search reference, company, email, TRN…"
            className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#00B4D8]"
            data-testid="rfq-search"
          />
        </div>
        <span className="text-xs text-[#6C757D]">{filtered.length} of {rfqs.length}</span>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Reference</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Company</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Contact</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Country</th>
              <th className="text-center px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Items</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Received</th>
              <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Open</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-[#6C757D]">
                <Loader2 className="w-6 h-6 mx-auto animate-spin" />
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-[#6C757D]">
                <Inbox className="w-10 h-10 mx-auto mb-2 opacity-40" />
                No RFQs yet. They will appear here as soon as visitors submit a quote from <b className="text-[#FF6B35]">/it-solutions</b>.
              </td></tr>
            )}
            {!loading && filtered.map((r) => (
              <tr
                key={r.id}
                onClick={() => setSelected(r)}
                className="hover:bg-slate-50 cursor-pointer"
                data-testid={`rfq-row-${r.reference}`}
              >
                <td className="px-4 py-3 font-mono text-[#023E8A] font-semibold">{r.reference}</td>
                <td className="px-4 py-3 text-[#2B2D42] font-medium">{r.company_name}</td>
                <td className="px-4 py-3 text-[#6C757D]">
                  <div className="font-medium text-[#2B2D42]">{r.contact_name}</div>
                  <div className="text-[11px]">{r.contact_email}</div>
                </td>
                <td className="px-4 py-3 text-[#6C757D]">{r.country || '—'}</td>
                <td className="px-4 py-3 text-center">
                  <Badge className="bg-[#FF6B35]/10 text-[#FF6B35] border border-[#FF6B35]/20 font-mono">
                    {(r.items || []).length}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-[#6C757D] text-[12px]">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <ArrowRight className="w-4 h-4 text-[#023E8A] inline" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <RFQDetailPanel rfq={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};

const Kpi = ({ label, value, icon: Icon, accent }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
      <Icon className="w-5 h-5" style={{ color: accent }} />
    </div>
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#6C757D] font-bold">{label}</div>
      <div className="text-2xl font-extrabold text-[#2B2D42]">{value}</div>
    </div>
  </div>
);

const RFQDetailPanel = ({ rfq, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="rfq-detail-panel">
    <div className="bg-white sm:rounded-2xl w-full max-w-3xl max-h-[95vh] flex flex-col shadow-2xl">
      <div className="px-6 py-4 bg-gradient-to-r from-[#023E8A] to-[#00B4D8] text-white sm:rounded-t-2xl flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-0.5 flex items-center gap-1.5">
            <Hash className="w-3 h-3" /> Quote Reference
          </div>
          <h3 className="text-lg font-extrabold font-mono">{rfq.reference}</h3>
        </div>
        <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="rfq-close-btn"><X className="w-5 h-5" /></button>
      </div>

      <div className="overflow-y-auto px-6 py-5 space-y-5">
        <Section title="Company">
          <Detail icon={Building2} label="Company name" value={rfq.company_name} />
          <Detail icon={Globe} label="Country" value={rfq.country || '—'} />
          <Detail icon={FileText} label="UAE TRN" value={rfq.company_trn || '—'} />
        </Section>

        <Section title="Contact">
          <Detail icon={Building2} label="Name" value={rfq.contact_name} />
          <Detail
            icon={Mail}
            label="Email"
            value={
              <a href={`mailto:${rfq.contact_email}?subject=Re: Your quote ${rfq.reference}`} className="text-[#00B4D8] hover:underline">
                {rfq.contact_email}
              </a>
            }
          />
          <Detail icon={Phone} label="Phone" value={rfq.contact_phone || '—'} />
        </Section>

        <Section title={`Items (${(rfq.items || []).length})`}>
          <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg overflow-hidden">
            {(rfq.items || []).map((it, i) => (
              <li key={i} className="p-3 flex items-center justify-between bg-white hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-[#2B2D42] truncate">{it.name}</div>
                  <div className="text-[11px] font-mono text-[#6C757D]">{it.sku}</div>
                </div>
                <Badge className="bg-[#FF6B35]/10 text-[#FF6B35] border border-[#FF6B35]/20 ml-2 font-mono">
                  × {it.quantity}
                </Badge>
              </li>
            ))}
          </ul>
        </Section>

        {rfq.notes && (
          <Section title="Notes">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-[#2B2D42] whitespace-pre-wrap leading-relaxed">
              {rfq.notes}
            </div>
          </Section>
        )}

        <Section title="Timeline">
          <Detail icon={Calendar} label="Received" value={fmtDate(rfq.created_at)} />
          <Detail icon={Clock} label="Status" value={<Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">{rfq.status || 'new'}</Badge>} />
        </Section>
      </div>

      <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex items-center justify-between sm:rounded-b-2xl gap-2">
        <a
          href={`mailto:${rfq.contact_email}?subject=Your%20BMP%20IT%20Solutions%20quote%20${rfq.reference}`}
          className="inline-flex items-center gap-2 text-sm text-[#00B4D8] hover:underline"
          data-testid="rfq-reply-link"
        >
          <Mail className="w-4 h-4" /> Reply by email
        </a>
        <a href="/admin/crm" target="_blank" rel="noreferrer">
          <Button variant="outline" data-testid="rfq-open-crm-btn">
            Open in CRM <ExternalLink className="w-3.5 h-3.5 ml-2" />
          </Button>
        </a>
      </div>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF6B35] border-b border-orange-100 pb-1.5">{title}</div>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const Detail = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 text-sm">
    <Icon className="w-4 h-4 text-[#6C757D] flex-shrink-0" />
    <span className="text-[#6C757D] w-32">{label}</span>
    <span className="text-[#2B2D42] font-medium">{value}</span>
  </div>
);

export default RFQInbox;
